import * as React from 'react';

import _ from 'lodash';
import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import type { ActionContext } from 'js/lib/ActionContext';

import { DropDown } from '@coursera/coursera-ui';
import { SvgContentRemoveCircle, SvgEdit, SvgTrash } from '@coursera/coursera-ui/svg';

import _t from 'i18n!nls/staff-management';

import 'css!./__styles__/RowActions';

import DropdownMenu from '../common/DropdownMenu';
import type { StaffManagementStoreV2 as StaffManagementStoreClass, StaffMembershipType } from '../store';
import { actions, selectors } from '../store';
import withFluxibleContextAsProps from '../withFluxibleContextAsProps';
import type { StaffTableRow } from './types';

export type Props = {
  /** Delete only the roles associated with the scope defined by type (e.g. only institution roles). */
  delTypeRoles?(): void;
  /** Delete all roles possible from the user from the scope defined by `type`. */
  delStaff?(): void;
  /** Edit one user referenced by their associated row. */
  edit?(): void;
  /** Row descriptor that should contain enough information to reference a staff member. */
  row: StaffTableRow;
  /** Provides content nuances for particular type of staff management. */
  type?: keyof typeof StaffMembershipType;
};

export const editDeleteLabels = (
  type?: keyof typeof StaffMembershipType
): { editLabel: string; delTypeRolesLabel: string } => {
  switch (type) {
    case 'partner':
      return {
        editLabel: _t('Edit institution role assignment'),
        delTypeRolesLabel: _t('Remove institution roles'),
      };
    case 'program':
      return {
        editLabel: _t('Edit program role assignment'),
        delTypeRolesLabel: _t('Remove program roles'),
      };
    case 'course':
      return {
        // This is not used in `teach` or `learn`
        editLabel: _t('Edit course role assignment'), // eslint-disable-line no-restricted-syntax
        delTypeRolesLabel: _t('Remove course roles'), // eslint-disable-line no-restricted-syntax
      };
    default:
      return {
        editLabel: '',
        delTypeRolesLabel: '',
      };
  }
};

export class RowActions extends React.Component<Props> {
  static e2eName = 'staff-management-staff-table-row-actions';

  render() {
    const { delStaff, delTypeRoles, edit, type } = this.props;
    const delLabel = _t('Remove staff');
    const { editLabel, delTypeRolesLabel } = editDeleteLabels(type);

    return (
      <div className="rc-RowActions" data-e2e={RowActions.e2eName}>
        <DropdownMenu>
          {({ hide }) => (
            <div>
              {edit && (
                <DropDown.Item
                  label={
                    <div className="action">
                      <span className="action-icon">
                        <SvgEdit title={editLabel} data-e2e="row-btn-edit" />
                      </span>
                      <span className="action-label">{editLabel}</span>
                    </div>
                  }
                  onClick={() => {
                    edit();
                    hide();
                  }}
                  rootClassName="rc-RowAction"
                />
              )}
              {delTypeRoles && (
                <DropDown.Item
                  label={
                    <div className="action">
                      <span className="action-icon">
                        <SvgContentRemoveCircle title={delTypeRolesLabel} data-e2e="row-btn-remove-type-roles" />
                      </span>
                      <span className="action-label">{delTypeRolesLabel}</span>
                    </div>
                  }
                  onClick={() => {
                    delTypeRoles();
                    hide();
                  }}
                  rootClassName="rc-RowAction"
                />
              )}
              {delStaff && (
                <DropDown.Item
                  label={
                    <div className="action">
                      <span className="action-icon">
                        <SvgTrash title={delLabel} data-e2e="row-btn-delete" />
                      </span>
                      <span className="action-label">{delLabel}</span>
                    </div>
                  }
                  onClick={() => {
                    delStaff();
                    hide();
                  }}
                  rootClassName="rc-RowAction"
                />
              )}
            </div>
          )}
        </DropdownMenu>
      </div>
    );
  }
}

export const storeConnector = (
  { StaffManagementStoreV2 }: { StaffManagementStoreV2: StaffManagementStoreClass },
  { executeAction, row }: $TSFixMe
) => {
  const state = StaffManagementStoreV2.get();
  const { fromId, type } = state;
  const membershipsByUserId = selectors.staffMemberships.getMembershipsByUserId(state);
  const membershipsByUserIdLimitedToType = selectors.staffMemberships.getMembershipsByUserIdLimitedByType(state);
  return {
    delStaff: () => {
      return executeAction(actions.delFlow.prompt, {
        fromId,
        type,
        users: [row],
      });
    },
    // If user has memberships within the scope of the component as well as outside the scope,
    // allow person at the keyboard the option to delete just the roles in scope. E.g. in
    // the institution staff view, if a user has 1 or more institution roles as well as course
    // or program roles, then they have the option to remove just the institution staff roles.
    delTypeRoles:
      _.get(membershipsByUserIdLimitedToType[row.id], 'length') &&
      _.get(membershipsByUserIdLimitedToType[row.id], 'length') !== _.get(membershipsByUserId[row.id], 'length')
        ? () => {
            return executeAction(actions.delFlow.promptTypeRolesOnly, {
              fromId,
              type,
              users: [row],
            });
          }
        : undefined,
    edit: () => {
      return executeAction(actions.editFlow.prompt, {
        fromId,
        type,
        users: [row],
        // For single edit, we assert a default set of roles for the edit form.
        roles: _.map(row?.roles, (role) => role.id),
      });
    },
    type,
  };
};

export const rowActionsHoC = compose<Props, Props>(
  withFluxibleContextAsProps(),
  connectToStores<Props, Props & ActionContext>(['StaffManagementStoreV2'], storeConnector)
);

export default rowActionsHoC(RowActions);
